import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import Close from '@mui/icons-material/Close';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { alpha, Box, Checkbox, Chip, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import _assign from 'lodash/assign';
import _uniq from 'lodash/uniq';
import styled from 'styled-components';

import { convertToTaxonomyCase, EVENTS, useTrack } from '@arrived/analytics';
import { useOfferingFilters } from '@arrived/contexts';
import { useGetMarketsQuery } from '@arrived/queries';

const StyledMarketSelect = styled(Select)`
  .MuiSelect-select {
    padding: ${({ theme }) => theme.spacing(1, 2)};
  }
` as typeof Select;

const StyledChip = styled(Chip)`
  &.MuiChip-root {
    background-color: ${({ theme }) => theme.palette.tertiary[500]};
    color: ${({ theme }) => theme.palette.neutrals.white};

    .MuiSvgIcon-root,
    .MuiSvgIcon-root:hover {
      color: ${({ theme }) => theme.palette.neutrals.white};
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root.Mui-selected {
    background-color: ${({ theme }) => alpha(theme.palette.tertiary[500], 0.1)};

    &:hover {
      background-color: ${({ theme }) => alpha(theme.palette.tertiary[500], 0.05)};
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    &:hover {
      background-color: ${({ theme }) => alpha(theme.palette.tertiary[500], 0.1)};
    }

    &.Mui-checked {
      color: ${({ theme }) => theme.palette.tertiary[500]};
    }
  }
`;

const ALL_LOCATIONS = {
  id: -1,
  title: <FormattedMessage id="all-locations" defaultMessage="All Locations" />,
} as const;

export const MarketDropdown = () => {
  const { offeringFilter, setOfferingFilter } = useOfferingFilters();
  const track = useTrack();

  const { data: marketsList = [], isSuccess } = useGetMarketsQuery({
    select: (data) => data.sort((market1, market2) => market1.title.localeCompare(market2.title)) ?? [],
  });

  const marketsFilter = useMemo(() => offeringFilter.marketId?.eq ?? [], [offeringFilter]);

  const marketIdsToTitles = useMemo(
    () => new Map(marketsList?.map((market) => [market.id, market.title])),
    [marketsList]
  );

  const setMarkets = useCallback((markets: number[]) => {
    setOfferingFilter('marketId', markets);
  }, []);

  const handleOnMarketsChange = useCallback((event: SelectChangeEvent<number[]>) => {
    const selection = event.target.value as number[];
    setMarkets(selection.includes(ALL_LOCATIONS.id) ? [] : _uniq(selection));
  }, []);

  return (
    <StyledMarketSelect
      disabled={!isSuccess || marketsList.length === 0}
      displayEmpty
      fullWidth
      IconComponent={KeyboardArrowDown}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        MenuListProps: {
          disablePadding: true,
        },
        sx: { maxHeight: '300px' },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      }}
      multiple
      value={marketsFilter}
      onChange={handleOnMarketsChange}
      renderValue={(selected) => (
        <>
          {selected.length === 0 ? (
            <FormattedMessage id="all-locations" defaultMessage="All Locations" />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((marketId) => {
                const onDelete = () => {
                  setMarkets(marketsFilter.filter((id) => id !== marketId));

                  track(EVENTS.OFFERING_FILTER_CLICKED, {
                    filter_name: convertToTaxonomyCase(title),
                  });
                };

                const title = marketIdsToTitles.get(marketId);

                return (
                  <StyledChip
                    clickable
                    deleteIcon={<Close />}
                    key={marketId}
                    label={title}
                    onClick={onDelete}
                    onDelete={onDelete}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                );
              })}
            </Box>
          )}
        </>
      )}
    >
      {[ALL_LOCATIONS, ...marketsList].map(({ id, title }) => (
        <StyledMenuItem key={id} value={id}>
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              variant: id === ALL_LOCATIONS.id ? 'body1.bold' : 'body1',
            }}
          />
          <StyledCheckbox
            checked={(id === ALL_LOCATIONS.id && marketsFilter.length === 0) || marketsFilter.includes(id)}
          />
        </StyledMenuItem>
      ))}
    </StyledMarketSelect>
  );
};
